/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  Card,
  Heading,
  ColumnGrid,
  NavLink,
  SvgIcon,
  Spinner,
  CenteredContainerDisplay,
  DeductionSummary
} from "../components"

import { IMPACT_REPORT_DATE } from '../constants'

import backgroundImage from '../assets/img/dash-bg.jpg'

const RIGHT_ARROW = <span style={{ fontSize: 20 }}>&rarr;</span>


class Dashboard extends React.Component {
  static propTypes = {
    data: PropTypes.object.isRequired,
    refetch: PropTypes.bool
  }

  state = {
    refetch: false,
    showPayslipFileUploadModal: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.error && !this.state.refetch) {
      this.setState({
        refetch: true
      })
      nextProps.data.refetch()
    }

    if (this.state.refetch) {
      this.setState({
        refetch: false
      })
    }
  }

  getDeductionCardBody = (deductions) => {
    if (deductions.length === 0) {
      return <div>No deductions ready for review yet</div>
    } else {
      return (
        <div style={{ height: 173 }}>
          {deductions.map(deduction => (
            <DeductionSummary deductionId={deduction.node.id} />
          ))}
        </div>
      )
    }
  }

  getEmployerDeductionsCard = (deductions) => (
    <Card
      title="Deductions"
      icon={
        <SvgIcon
          variant="picsaos"
          symbol="deductions"
          size="medium"
        />
      }
      info={
        <div className="slds-grid">
          <div className="card__value-label">
            <b>{deductions.length}</b>
            <span className="slds-p-left_xx-small">open</span>
          </div>
        </div>
      }
      body={this.getDeductionCardBody(deductions.length > 1
        ? deductions.slice(0, 2)
        : deductions
      )}
      footer={
        <div className="slds-text-align_left slds-p-left_medium">
          {
            <span>
              <NavLink to={`/os/operations/general/deductions`}>
                <b>
                  View all deductions (past and present)
                  &nbsp;&nbsp;
                  {RIGHT_ARROW}
                </b>
              </NavLink>
            </span>
          }
        </div>
      }
      yScrollable={true}
      paddingTRL={true}
    />
  )

  getImpactReportCard = () => (
    <ImpactReport />
  )

  getSupportCard = () => (
    <Card
      title="Support"
      icon={
        <SvgIcon
          variant="picsaos"
          symbol="support"
          size="medium"
          colour='grey'
        />
      }
      body={
        <ul className='slds-p-top_medium slds-p-bottom_medium'>
          <li className="slds-p-bottom_medium slds-border_bottom">
            <b>0 Open cases</b> <span className="slds-float_right">
              {RIGHT_ARROW}  {/* TODO: wrap in NavLink when Support card in use */}
            </span>
          </li>
          <li className="slds-p-top_medium">
            <b>Create a new support case</b> <span className="slds-float_right">
              {RIGHT_ARROW}  {/* TODO: wrap in NavLink when Support card in use */}
            </span>
          </li>
        </ul>
      }
      className="inactive"
    />
  )

  togglePayslipFileUploadModal = () => (
    this.setState(prevState => ({
      showPayslipFileUploadModal: !prevState.showPayslipFileUploadModal
    }))
  )

  render() {
    if (this.props.data.loading) {
      return <Spinner type="brand" size="large" />
    }

    if (this.props.data.error) {
      return (
        <CenteredContainerDisplay
          content={
            <div>
              <SvgIcon
                variant="utility"
                symbol="warning"
                size="large"
                colour="error"
                className="slds-align_absolute-center"
              />
              <Heading className="slds-p-top_medium u-no-margin__b">
                Sorry, this page failed to load the necessary data and therefore cannot be displayed.<br />
                Please refresh your browser and if this message continues to appear please contact us.
              </Heading>
            </div>
          }
          yAdjustPercentage={-20}
        />
      )
    }

    let deductions = this.props.data.workflowruns.edges
    .filter((workflowrun) => (
      workflowrun.node.atStep >= 8
    ))

    let deductionsCard = this.getEmployerDeductionsCard(deductions)
    let impactReportCard = this.getImpactReportCard()
    let supportCard = this.getSupportCard()

    return (
      <div
        className="background-image_cover"
        style={{ backgroundImage: `url(${backgroundImage})`, height: '100vh' }}>

        <ColumnGrid
          items={[
            [
              deductionsCard,
              <Card
                title="Downloads"
                icon={
                  <SvgIcon
                    variant="picsaos"
                    symbol="download"
                    size="medium"
                  />
                }
                body={
                  <ul className='slds-p-top_small'>
                    <li>
                      <div className="slds-border_bottom slds-p-bottom_small" style={{ lineHeight: 1.5 }}>
                        <div className='u-font-weight-bold u-font-size-larger'>
                          Application forms
                        </div>
                        <div className='clickable clickable-link slds-grid slds-p-top_x-small' style={{ color: '#1d9652', gridAutoFlow: 'column', alignItems: 'center'}}>
                          <NavLink to={`/os/downloads`}>
                            Download applications and other forms
                          </NavLink>
                          <NavLink to={`/os/downloads`} className='slds-col_bump-left'>
                            <SvgIcon
                              variant="utility"
                              symbol='download'
                              size='xx-small'
                              colour='#1d9652'
                            />
                          </NavLink>
                        </div>
                      </div>
                    </li>
                  </ul>
                }
              />
            ],
            [ impactReportCard ],
            [
              <Card
                title="People"
                icon={
                  <SvgIcon
                    variant="picsaos"
                    symbol="people"
                    colour='grey'
                    size="medium"
                  />
                }
                body={
                  <ul className='slds-p-bottom_medium'>
                    <li>
                      <div className="slds-border_bottom slds-p-bottom_small" style={{ lineHeight: 1.5 }}>
                        <div className='u-font-weight-bold u-font-size-larger'>
                          Manage
                        </div>
                        <div className='slds-grid slds-p-top_x-small' style={{ color: 'lightgrey', gridAutoFlow: 'column', alignItems: 'center' }}>
                          <div>
                            See employee listing, message individuals
                          </div>
                          <div className='slds-col_bump-left'>
                            {RIGHT_ARROW}
                          </div>
                        </div>
                        <div className='slds-grid slds-p-top_x-small' style={{ color: 'lightgrey', gridAutoFlow: 'column', alignItems: 'center' }}>
                          <div>
                            Add and onboard new employees
                          </div>
                          <div className='slds-col_bump-left'>
                            {RIGHT_ARROW}
                          </div>
                        </div>
                      </div>
                    </li>
                    <li className="slds-p-top_medium">
                      <div className="slds-border_bottom slds-p-bottom_small" style={{ lineHeight: 1.5 }}>
                        <div className='u-font-weight-bold u-font-size-larger'>
                          Payroll
                        </div>
                        <div className='slds-grid slds-p-top_x-small' style={{ color: 'inherit', gridAutoFlow: 'column', alignItems: 'center' }}>
                          Upload payroll file, generate payslips
                          <SvgIcon
                            variant="utility"
                            symbol='upload'
                            size='xx-small'
                            colour='light'
                            className='slds-col_bump-left'
                          />
                        </div>
                      </div>
                    </li>
                  </ul>
                }
                className='inactive'
              />,
              supportCard
            ]
          ]}
        />
      </div>
    )
  }
}

class ImpactReport extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      graphWidth: 300
    }
  }

  componentDidMount() {
    this.updateGraphWidthToMatchParent()
  }

  updateGraphWidthToMatchParent = () => {
    let graphParent = this.impactReport
    let graphParentPadding = window.getComputedStyle(graphParent, null).getPropertyValue('padding-left').replace('px','')
    this.setState({
      graphWidth: graphParent.offsetWidth - (parseInt(graphParentPadding, 10) + 100)
    })
  }

  getImpactReportCardBody = () => (
    <div>
      {/* TODO: add content */}
    </div>
  )

  render() {
    return (
      <div id="graphParent" ref={(ref) => {this.impactReport = ref}}>
        <Card
          title="Impact report"
          icon={
            <SvgIcon
              variant="picsaos"
              symbol="reports"
              size="medium"
            />
          }
          info={`Updated ${moment(IMPACT_REPORT_DATE).format('DD MMM')}`}
          // body={this.getImpactReportCardBody()}
          footer={
            <div className="slds-text-align_left slds-p-left_medium">
              <span>
                <NavLink
                  to={`/os/insights/impact/${localStorage.getItem("employer")}`}>
                  <b>
                    View full report(s)
                    &nbsp;&nbsp;
                    {RIGHT_ARROW}
                  </b>
                </NavLink>
              </span>
            </div>
          }
        />
      </div>
    )
  }
}

const getOpenDeductions = gql`
  query {
    workflowruns(status: "processing", workflow_Slug: "deductions") {
      edges {
        node {
          id
          parameters
          atStep
        }
      }
    }
    me {
      id
      contacts {
        edges {
          node {
            id
            employer {
              id
            }
          }
        }
      }
    }
  }
`

const withData = graphql(getOpenDeductions)

export default withData(Dashboard)



// WEBPACK FOOTER //
// ./src/containers/Dashboard.js